import React, { Component } from 'react';

import Constants from '../../Constants.js';
import Seasons from '../../Seasons.js';
import Typography from '../../Typography.js';
import Utils from '../../Utils.js';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFire, faMedal, faStar, faTrophy } from '@fortawesome/free-solid-svg-icons'

import '../../styles/modals/ProfileModal.css';

class ProfileModal extends Component {
  // The classic face is fetched lazily, so a player still on the default has
  // not loaded it -- and the styles section has to show it.
  componentDidMount() {
    Typography.loadClassicFont();
  }

  handleTypographyChange = (event) => {
    this.props.onTypographyChange(event.target.value);
  }

  // Each option is set in the face it selects, so the two read as specimens
  // rather than as labels describing something the player cannot see. That
  // leaves them with the same words, which is fine to look at and useless to
  // hear -- hence the label carried on the input itself.
  renderTypographyOption = (value, label) => {
    return (
      <label className="profile-typography-option">
        <input
          type="radio"
          name="typography"
          value={value}
          aria-label={label}
          checked={this.props.typography === value}
          onChange={this.handleTypographyChange}
        />
        <span className="radio-span"></span>
        <span className={`profile-typography-name profile-typography-sample-${value}`}>Ten Words</span>
      </label>
    );
  }

  // Trophy counts are counted from the season results themselves rather than
  // read from a stored tally, so the count above can never disagree with the
  // finishes listed below it.
  getTrophyCounts = () => {
    const counts = Constants.ACCOLADE_COLORS.map(() => 0);

    this.props.seasons.forEach((season) => {
      if (season.rank < counts.length) {
        counts[season.rank]++;
      }
    });

    return counts;
  }

  // Weekly medals are counted server-side from the settled weeks, for the same
  // reason trophies are counted from the settled seasons: the tally and the
  // record it comes from cannot disagree if there is only one of them.
  getMedalCounts = () => {
    const counts = Constants.ACCOLADE_COLORS.map(() => 0);

    (this.props.medals || []).forEach((count, i) => {
      if (i < counts.length) {
        counts[i] = count;
      }
    });

    return counts;
  }

  renderAccoladeColumn = (icon, counts, kind, describe) => {
    return (
      <div className="profile-accolade-column">
        {counts.map((count, i) => {
          return (
            <div className="profile-accolade-group" key={`profile_${kind}_group_${i}`}>
              <div className="profile-accolade-icon-div">
                <FontAwesomeIcon
                  icon={icon}
                  className={`accolade-icon profile-accolade-${Constants.ACCOLADE_COLORS[i]}`}
                  title={describe(i)}
                />
              </div>
              <div className="profile-accolade-count">
                {count}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // The streak sits with the accolades because it is one: the tally a player
  // keeps rather than a placing they took. The streak in progress is the headline
  // number, as it is in the header, with the record under it -- and it carries
  // the header's tooltip word for word, so the flame says the same thing
  // wherever it is hovered.
  renderStreak = () => {
    const { current, longest } = this.props.streaks;

    return (
      <div
        className="profile-streak"
        title={Utils.assembleStreakString(current, this.props.streakAtRisk)}
      >
        <div className="profile-streak-current">
          <FontAwesomeIcon icon={faFire} className="accolade-icon profile-streak-icon" />
          <div className="profile-streak-count">{current}</div>
        </div>
        <div className="profile-streak-longest">
          best {longest}
        </div>
      </div>
    );
  }

  renderSeasonRow = (season, index) => {
    const parity = index % 2 === 0 ? "even" : "odd";
    const placing = `${Utils.getOrdinal(season.rank + 1)} of ${season.playerCount}`;

    return (
      <div className={`profile-season-row profile-season-row-${parity}`} key={`profile_season_${season.season}`}>
        <div className="profile-season-name">
          Season {season.season}
        </div>
        <div className="profile-season-placing">
          { season.rank < Constants.ACCOLADE_COLORS.length &&
            <FontAwesomeIcon
              icon={faTrophy}
              className={`accolade-icon profile-accolade-${Constants.ACCOLADE_COLORS[season.rank]}`}
              title={`${Utils.capitalize(Constants.ACCOLADE_COLORS[season.rank])} trophy`}
            />
          }
          <span className="profile-season-placing-text">{placing}</span>
        </div>
      </div>
    );
  }

  render() {
    const gameNoun = this.props.profile.games === 1 ? "game" : "games";
    // Until the first season begins, this is the profile exactly as it was
    // before seasons existed: stars alone, and no season anything.
    const status = Seasons.getSeasonStatus(Utils.getDateStringToday());

    return (
      <div className={`profile-modal${status.started ? " profile-modal-seasonal" : ""}`}>
        <div className="profile-header">
          {this.props.profile.name}
        </div>
        <div className="profile-games">
          {this.props.profile.games} {gameNoun} played
        </div>

        <fieldset className="profile-fieldset">
          <legend className="profile-legend">
            Accolades
          </legend>
          <div className="profile-fieldset-inner">
            {this.renderAccoladeColumn(
              faStar,
              this.props.profile.stars || [],
              "star",
              (i) => `${Utils.capitalize(Constants.ACCOLADE_COLORS[i])} stars are awarded for achieving a day's ${Constants.SCORE_DESCRIPTORS[i]} score.`
            )}
            { status.started && this.renderAccoladeColumn(
              faMedal,
              this.getMedalCounts(),
              "medal",
              (i) => `${Utils.capitalize(Constants.ACCOLADE_COLORS[i])} medals are awarded for achieving a week's ${Constants.SCORE_DESCRIPTORS[i]} total.`
            )}
            { status.started && this.renderAccoladeColumn(
              faTrophy,
              this.getTrophyCounts(),
              "trophy",
              (i) => `${Utils.capitalize(Constants.ACCOLADE_COLORS[i])} trophies are awarded for achieving a season's ${Constants.SCORE_DESCRIPTORS[i]} total.`
            )}
          </div>
          {this.renderStreak()}
        </fieldset>

        { status.started &&
          <fieldset className="profile-fieldset profile-fieldset-seasons">
            <legend className="profile-legend">
              Seasons
            </legend>
            <div className="profile-seasons">
              { this.props.seasons.length > 0
                ? this.props.seasons.map(this.renderSeasonRow)
                : <div className="profile-seasons-empty">No finished seasons yet.</div>
              }
            </div>
          </fieldset>
        }

        <fieldset className="profile-fieldset profile-fieldset-styles">
          <legend className="profile-legend">
            Styles
          </legend>
          <div className="profile-fieldset-inner profile-typography">
            {this.renderTypographyOption(Constants.TYPOGRAPHY_MODERN, "Current styles")}
            {this.renderTypographyOption(Constants.TYPOGRAPHY_CLASSIC, "Classic styles")}
          </div>
          { !!this.props.typographyError &&
            <div className="profile-typography-error">
              {this.props.typographyError}
            </div>
          }
        </fieldset>

        <button className="profile-modal-close" onClick={this.props.onClose}>
          Close
        </button>
      </div>
    );
  }
}

ProfileModal.defaultProps = {
  seasons: [],
  medals: [],
  streaks: Constants.NO_STREAKS,
  streakAtRisk: false,
  typography: Constants.TYPOGRAPHY_MODERN,
  typographyError: null,
  onTypographyChange: () => {}
};

export default ProfileModal;
